// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useState } from 'react';
import { IStackStyles, Stack } from '@fluentui/react';
import { Welcome } from '../components/Welcome';
import { LandingPageButtons } from '../components/LandingPageButtons';
import { useIsMobile } from 'hooks/useIsMobile';
import { DeepLinkDrawer } from '../components/Drawer/DeepLinkDrawer';
import { generateACSDeepLink, generateTeamsDeepLink, openDeepLinkInNewWindow } from 'utils/generateDeepLink';
import { createNewACSMeeting, NewAcsMeetingDetails } from 'utils/createNewACSMeeting';
import { PSTNCallInfo, PSTNCallModal } from 'components/PSTNCallModal';
import { useFunctionsAppToken, useIsSignedIn, useUserName } from 'hooks/useUser';
import { JoinCallInfo } from 'components/YourDay';
import { JoinTeamsMeetingModal } from 'components/JoinTeamsMeetingModal';

export const Landing = (props: {
  onStartACSCall: (meetingDetails: NewAcsMeetingDetails) => void;
  onJoinFromLinkClick: (callInfo: JoinCallInfo) => Promise<void>;
  onStartPSTNCallClick: (callInfo: PSTNCallInfo) => Promise<void>;
  onSpeakToBot: () => Promise<void>;
}): JSX.Element => {
  const [showDialpadModal, setShowDialpadModal] = useState(false);
  const [showJoiningTeamsModal, setShowJoiningTeamsModal] = useState(false);
  const [deepLinkDetails, setDeepLinkDetails] = useState<
    { deepLink: string; continueOnBrowser: () => void } | undefined
  >();
  const isMobile = useIsMobile();

  const userDisplayName = useUserName();
  const functionsAppToken = useFunctionsAppToken();
  const isSignedIn = useIsSignedIn();

  const startACall = async (): Promise<void> => {
    if (functionsAppToken === undefined || !userDisplayName) {
      throw new Error('Incorrect user details to create a new meeting');
    }

    const meetingDetails = await createNewACSMeeting(functionsAppToken);
    if (isMobile) {
      setDeepLinkDetails({
        deepLink: await generateACSDeepLink(meetingDetails.groupId, userDisplayName, functionsAppToken),
        continueOnBrowser: () => props.onStartACSCall(meetingDetails)
      });
    } else {
      props.onStartACSCall(meetingDetails);
    }
  };

  const joinFromTeamsLink = async (joinCallInfo: JoinCallInfo): Promise<void> => {
    const continueOnBrowser = async (): Promise<void> => {
      await props.onJoinFromLinkClick(joinCallInfo);
    };

    if (isMobile) {
      if (!userDisplayName || functionsAppToken === undefined) {
        throw new Error('Incorrect user details to create a mobile deep link');
      }

      const deepLink = await generateTeamsDeepLink(joinCallInfo.url, userDisplayName, functionsAppToken);
      setShowJoiningTeamsModal(false);
      setDeepLinkDetails({ deepLink, continueOnBrowser });
    } else {
      setShowJoiningTeamsModal(false);
      await continueOnBrowser();
    }
  };

  const makePSTNCall = async (pstnCallInfo: PSTNCallInfo): Promise<void> => {
    setShowDialpadModal(false);
    await props.onStartPSTNCallClick(pstnCallInfo);
  };

  return (
    <Stack
      tokens={{ childrenGap: '2rem' }}
      styles={landingContainerStyles}
      verticalFill
      verticalAlign={isSignedIn ? undefined : 'center'}
    >
      <Stack.Item>
        <Welcome isSignedIn={isSignedIn} name={userDisplayName} />
      </Stack.Item>
      {isSignedIn && (
        <Stack.Item>
          <Stack
            tokens={{ childrenGap: '2rem' }}
            styles={{
              root: {
                width: '100%',
                maxWidth: '100vw',
                paddingLeft: '1rem',
                paddingRight: '1rem'
              }
            }}
          >
            <Stack.Item
              styles={{
                root: {
                  overflow: 'auto'
                }
              }}
            >
              <LandingPageButtons
                onStartCallClick={startACall}
                onJoinFromLinkClick={() => setShowJoiningTeamsModal(true)}
                onSpeakToBotClick={props.onSpeakToBot}
                onPSTNCallClick={() => setShowDialpadModal(true)}
              />
            </Stack.Item>
            {/* <Stack.Item>
              <YourDay onJoinCallClick={joinFromTeamsLink} />
            </Stack.Item> */}
          </Stack>
        </Stack.Item>
      )}
      {isSignedIn && (
        <JoinTeamsMeetingModal
          isOpen={showJoiningTeamsModal}
          onCancel={() => setShowJoiningTeamsModal(false)}
          onJoinCall={(joinIdentity, url) => joinFromTeamsLink({ joinIdentity, url })}
        />
      )}
      {isSignedIn && (
        <PSTNCallModal
          isOpen={showDialpadModal}
          onCancel={() => setShowDialpadModal(false)}
          onStartCall={makePSTNCall}
          functionsAppToken={functionsAppToken}
        />
      )}

      {deepLinkDetails && (
        <DeepLinkDrawer
          onContinueInBrowser={() => {
            deepLinkDetails.continueOnBrowser();
            setDeepLinkDetails(undefined);
          }}
          onDeepLinkClick={() => {
            openDeepLinkInNewWindow(deepLinkDetails.deepLink);
          }}
          onDismiss={() => {
            setDeepLinkDetails(undefined);
          }}
        />
      )}
    </Stack>
  );
};

const landingContainerStyles: IStackStyles = {
  root: {
    paddingBottom: '2rem'
  }
};
